<script setup lang="ts">
// Mobile-only sticky call to action. It appears once the visitor has scrolled
// past the hero and hides again whenever any claim field (hero, inline,
// bottom) is on screen, so there is never a duplicate CTA in view.
// Reported as `claim_click` with location "sticky" (guides/28).
const { trackGoal } = useDatafast()

const visible = ref(false)

onMounted(() => {
  const claims = Array.from(document.querySelectorAll<HTMLElement>('[id^="claim-username-"]'))
  const onScreen = new Map<Element, boolean>()
  let pastHero = false

  const update = () => {
    visible.value = pastHero && ![...onScreen.values()].some(Boolean)
  }

  const observer = new IntersectionObserver((entries) => {
    for (const e of entries) onScreen.set(e.target, e.isIntersecting)
    update()
  })
  for (const el of claims) observer.observe(el)

  const onScroll = () => {
    const next = window.scrollY > window.innerHeight * 0.6
    if (next !== pastHero) {
      pastHero = next
      update()
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()

  onUnmounted(() => {
    observer.disconnect()
    window.removeEventListener('scroll', onScroll)
  })
})

function onClick() {
  trackGoal('claim_click', { location: 'sticky', state: 'cta' })
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-4 opacity-0"
    leave-active-class="transition duration-200 ease-in"
    leave-to-class="translate-y-4 opacity-0"
  >
    <div
      v-if="visible"
      class="fixed inset-x-3 z-40 flex items-center justify-between gap-3 rounded-2xl border border-default bg-elevated/95 py-2.5 pl-4 pr-2.5 shadow-lg backdrop-blur md:hidden"
      style="bottom: max(0.75rem, env(safe-area-inset-bottom))"
    >
      <p class="text-sm leading-tight text-muted">
        <span class="block font-semibold text-highlighted">Your page, your way</span>
        Free forever
      </p>
      <UButton
        label="Claim your page"
        color="primary"
        size="lg"
        class="rounded-xl"
        to="/signup"
        @click="onClick"
      />
    </div>
  </Transition>
</template>
